/**
 * voice-v8 — der Anzeige-Vertrag einer Werkzeugkarte. (15.10.2026, Plan „Karten zu 100 % einheitlich“, Welle 1)
 *
 * Martin: *„Ich möchte einen einfachen Auftrag, und dann brauche ich nicht hunderttausend Cards.“* Ein Blender-Auftrag
 * erzeugte 8 Karten; 157 von 301 Werkzeugen fielen in die generische Karte, die das rohe Modell-Ergebnis ausbreitete.
 * Die Ursache in einem Satz: die Karte hing am Rohergebnis des Modells statt an einem Anzeige-Vertrag.
 *
 * Darum hier drei Dinge, geteilt zwischen Server und Gerät:
 *
 *   KartenPolitik  was ein Werkzeug im Verlauf zeigt — "stumm" (nichts), "schritt" (eine leise Zeile in den Schritten des
 *                  Zuges), "karte" (eine Karte, weil Martin das Ergebnis sehen oder bedienen muss). Je Werkzeug EINE
 *                  Zeile im Register (server/werkzeug-register-daten.ts), abgeleitet nach shared/karten-politik-daten.ts.
 *   Familie        wozu das Werkzeug gehört. Je Zug und Familie höchstens EINE Anzeige-Karte; weitere Aufrufe hängen ihre
 *                  Zeile an (frontend/lib/anzeige-karte.ts).
 *   Anzeige        was die Karte zeigt — gebaut vom Server (server/werkzeug-anzeige.ts) aus STRUKTURIERTEN Feldern, nie
 *                  aus Sätzen an das Modell. Das Gerät rendert nur.
 *
 * ⚠️ Das Rohergebnis (`result`) bleibt unverändert am Ereignis: das Modell und die Sonderkarten (Rechnung, Route, Wetter,
 *    Lied …) brauchen es. `anzeige` kommt dazu, sie ersetzt nichts.
 * ⚠️ Eine gespeicherte Anzeige kommt aus der Datenbank zurück — das Gerät prüft sie mit `leseAnzeige` und kappt sie,
 *    statt ihr blind zu glauben.
 */

/** Was ein Werkzeug im Verlauf zeigt. */
export type KartenPolitik = "stumm" | "schritt" | "karte";

export const KARTEN_POLITIKEN: readonly KartenPolitik[] = ["stumm", "schritt", "karte"];

/** Die Familien — in dieser Reihenfolge, mit dem Kopf ihrer gemeinsamen Karte. */
export const FAMILIEN_TITEL = {
  blender: "Blender",
  modelle: "3D-Modelle",
  mac: "Mac",
  browser: "Browser",
  web: "Web",
  reise: "Unterwegs",
  alltag: "Alltag",
  gedaechtnis: "Gedächtnis",
  mail: "E-Mail",
  kalender: "Kalender",
  kontakte: "Kontakte",
  rechnung: "Rechnungen",
  datei: "Dateien",
  medien: "Medien",
  koerper: "Körper",
  lernen: "Lernen",
  shell: "Server",
  einstellung: "Einstellungen",
  system: "Vespera",
  ticket: "Aufgaben",
  webseite: "Webseiten",
  newsletter: "Newsletter",
  verwaltung: "Verwaltung",
  sonst: "Erledigt",
} as const;

export type Familie = keyof typeof FAMILIEN_TITEL;

export const FAMILIEN: readonly Familie[] = Object.keys(FAMILIEN_TITEL) as Familie[];

export function istFamilie(x: unknown): x is Familie {
  return typeof x === "string" && Object.hasOwn(FAMILIEN_TITEL, x);
}

export function istKartenPolitik(x: unknown): x is KartenPolitik {
  return x === "stumm" || x === "schritt" || x === "karte";
}

/** Der Stand eines Aufrufs — aus den Feldern, nie aus einem Satz. */
export type AnzeigeStand = "ok" | "fehler" | "wartet" | "laeuft";

/** Was eine Karte zeigt. Höchstens 5 Zeilen, je höchstens 120 Zeichen. */
export type Anzeige = {
  familie: string;
  titel: string;
  zeile?: string;
  stand: AnzeigeStand;
  zeilen?: string[];
};

export const ANZEIGE_ZEILEN_MAX = 5;
export const ANZEIGE_ZEICHEN_MAX = 120;

/** Das Wort für einen Stand, wenn die Anzeige keine eigene Zeile hat. */
export const STAND_WORT: Readonly<Record<AnzeigeStand, string>> = {
  ok: "erledigt",
  fehler: "fehlgeschlagen",
  wartet: "wartet auf dein Ja",
  laeuft: "läuft noch",
};

/** Eine Zeile auf das Maß: Leerraum zusammengezogen, höchstens ANZEIGE_ZEICHEN_MAX Zeichen (mit „…“). */
export function kappeZeile(s: string, max = ANZEIGE_ZEICHEN_MAX): string {
  const eine = s.replace(/\s+/g, " ").trim();
  return eine.length <= max ? eine : `${eine.slice(0, max - 1).trimEnd()}…`;
}

/**
 * Eine Anzeige aus unbekannter Quelle lesen (Ereignis, Verlauf, Gerätespeicher) — oder null.
 * Kappt Zeilen und Länge; was nicht passt, fällt weg statt durchzurutschen.
 */
export function leseAnzeige(x: unknown): Anzeige | null {
  if (!x || typeof x !== "object") return null;
  const o = x as Record<string, unknown>;
  if (typeof o.titel !== "string" || !o.titel.trim()) return null;
  if (o.stand !== "ok" && o.stand !== "fehler" && o.stand !== "wartet" && o.stand !== "laeuft") return null;
  const familie = istFamilie(o.familie) ? o.familie : "sonst";
  const a: Anzeige = { familie, titel: kappeZeile(o.titel, 60), stand: o.stand };
  if (typeof o.zeile === "string" && o.zeile.trim()) a.zeile = kappeZeile(o.zeile);
  if (Array.isArray(o.zeilen)) {
    const zeilen = o.zeilen
      .filter((z): z is string => typeof z === "string" && z.trim().length > 0)
      .slice(0, ANZEIGE_ZEILEN_MAX)
      .map((z) => kappeZeile(z));
    if (zeilen.length > 0) a.zeilen = zeilen;
  }
  return a;
}
